import { formatPatientAge, getFormattedISODateTime } from '../assets/Utils';
import { formatAbnormalVitalsLabel } from '../utils/abnormalVitals';

export const VISIT_REASON_OPTIONS = [
  { id: 'consultation', name: 'Consultation' },
  { id: 'follow_up', name: 'Follow Up' },
  { id: 'follow_up_billable', name: 'Follow Up (Billable)' },
  { id: 'referral', name: 'Referral' },
  { id: 'emergency', name: 'Emergency' },
  { id: 'admission', name: 'Admission' },
  { id: 'procedure', name: 'Procedure' },
  { id: 'lab_only', name: 'Lab Only' },
  { id: 'imaging', name: 'Imaging' },
  { id: 'pharmacy', name: 'Pharmacy' },
  { id: 'antenatal_care', name: 'Antenatal Care' },
  { id: 'maternity_delivery', name: 'Maternity Delivery' },
  { id: 'vaccination', name: 'Vaccination' },
  { id: 'medical_examination', name: 'Medical Examination' },
  { id: 'other', name: 'Other' },
];

export const INTAKE_SYMPTOM_OPTIONS = [
  { id: 'fever', label: 'Fever' },
  { id: 'cough', label: 'Cough' },
  { id: 'chest_pain', label: 'Chest pain' },
  { id: 'shortness_of_breath', label: 'Shortness of breath' },
  { id: 'headache', label: 'Headache' },
  { id: 'vomiting', label: 'Vomiting' },
  { id: 'diarrhea', label: 'Diarrhea' },
  { id: 'abdominal_pain', label: 'Abdominal pain' },
  { id: 'weakness', label: 'Weakness' },
  { id: 'injury', label: 'Injury' },
  { id: 'pain_frequent_urination', label: 'Pain/frequent urination' },
  { id: 'bleeding', label: 'Bleeding' },
  { id: 'pregnancy_complaint', label: 'Pregnancy complaint' },
];

const NURSE_INTAKE_TAB = 'Nurse Intake';

export const hasPrescriptionEntries = (prescriptionItems) => {
  if (!Array.isArray(prescriptionItems)) return false;

  return prescriptionItems.some((item) => {
    if (!item) return false;
    if (typeof item === 'string') return item.trim().length > 0;
    if (typeof item !== 'object') return false;

    const medicationName = item.name || item.item_name || item.medicine || item.medication;
    if (typeof medicationName === 'string' && medicationName.trim().length > 0) {
      return true;
    }

    return Object.values(item).some((value) => {
      if (typeof value === 'string') return value.trim().length > 0;
      return value !== null && value !== undefined;
    });
  });
};

export const validatePatientVisitForm = (formData) => {
  const diagnosis = (formData?.diagnosis || '').trim();
  const hasMedication = hasPrescriptionEntries(formData?.prescription_items);

  if (!diagnosis && hasMedication) {
    return {
      isValid: false,
      message: 'A diagnosis is required to prescribe medication.',
    };
  }

  const intakeSymptoms = Array.isArray(formData?.intake_symptoms) ? formData.intake_symptoms : [];
  const hasIntakeContent = (formData?.chief_complaint || '').trim()
    || (formData?.symptom_onset || '').trim()
    || intakeSymptoms.length > 0
    || (formData?.known_illness || '').trim()
    || (formData?.allergy || '').trim()
    || (formData?.travel_outside_sl_30_days || '').trim();

  if (hasIntakeContent && !(formData?.chief_complaint || '').trim()) {
    return {
      isValid: false,
      message: 'Main complaint is required when recording intake information.',
    };
  }

  return { isValid: true };
};

const isDisabled = (mode, fieldName, overrides = {}) => {
  if (Object.prototype.hasOwnProperty.call(overrides, fieldName)) {
    return overrides[fieldName];
  }
  if (mode === 'doctor') {
    // Keep visit metadata locked for doctors; vitals editability is
    // controlled by FormModal via /patient-visits/vitals Edit permission.
    return ['visit_date', 'assigned_doctor'].includes(fieldName);
  }
  return false;
};

export const configurePatientVisitDropdowns = (config) => {
  config.addDropdownField({
    name: 'assigned_doctor',
    endpoint: '/hospital-portal/api/employees/doctors',
    valueField: 'doctor_name',
    labelField: 'doctor_name',
  });

  config.addStaticDropdownField({
    name: 'branch',
    options: [
      { id: 'Pultney', name: 'Pultney' },
      { id: 'Kissy', name: 'Kissy' },
    ],
  });

  config.addStaticDropdownField({
    name: 'visit_reason',
    options: VISIT_REASON_OPTIONS,
  });
};

export const configurePatientVisitFields = (config, { patient = {}, mode = 'full', disabledOverrides = {} } = {}) => {
  const disabled = (fieldName) => isDisabled(mode, fieldName, disabledOverrides);

  config
    .addField({ name: 'visit_id', label: 'Visit ID', type: 'number', identifier: true, disabled: true, tab: 'Visit Details' })
    .addField({
      name: 'patient_summary',
      label: 'Patient',
      type: 'text',
      disabled: true,
      tab: 'Visit Details',
      className: 'field-highlight',
      onInitialize: (value, data) => {
        const p = data.Patient || patient || {};
        const name = `${p.patient_first_name || ''} ${p.patient_last_name || ''}`.trim();
        const age = formatPatientAge(p.patient_dob);
        const sex = p.patient_sex || '';
        const company = p.company || 'Private';
        return [name, age, sex, company].filter(Boolean).join(', ');
      },
    })
    .addField({
      name: 'patient_id',
      label: 'Patient ID',
      type: 'number',
      tab: 'Visit Details',
      defaultValue: patient?.patient_id,
      visible: false,
    })
    .addField({
      name: 'visit_date',
      label: 'Visit Date',
      type: 'datetime-local',
      required: true,
      tab: 'Visit Details',
      defaultValue: getFormattedISODateTime(),
      disabled: disabled('visit_date'),
    })
    .addField({
      name: 'branch',
      label: 'Branch',
      type: 'dropdown',
      required: true,
      tab: 'Visit Details',
    })
    .addField({
      name: 'visit_reason',
      label: 'Visit Reason',
      type: 'dropdown',
      required: true,
      tab: 'Visit Details',
      defaultValue: 'Consultation',
    })
    .addField({
      name: 'assigned_doctor',
      label: 'Assigned Doctor',
      type: 'dropdown',
      required: true,
      tab: NURSE_INTAKE_TAB,
      defaultValue: 'Not Assigned',
      disabled: disabled('assigned_doctor'),
    })
    .addField({
      name: 'chief_complaint',
      label: 'Main Complaint',
      type: 'text',
      tab: NURSE_INTAKE_TAB,
      disabled: disabled('chief_complaint'),
    })
    .addField({
      name: 'symptom_onset',
      label: 'When did it start?',
      type: 'text',
      tab: NURSE_INTAKE_TAB,
      disabled: disabled('symptom_onset'),
    })
    .addField({
      name: 'intake_symptoms',
      label: 'Symptoms',
      type: 'checkbox-group',
      tab: NURSE_INTAKE_TAB,
      options: INTAKE_SYMPTOM_OPTIONS,
      defaultValue: [],
      disabled: disabled('intake_symptoms'),
    })
    .addField({
      name: 'known_illness',
      label: 'Known Illness',
      type: 'text',
      tab: NURSE_INTAKE_TAB,
      placeholder: 'List any known or chronic illnesses (Leave blank if no known illnesses)',
      disabled: disabled('known_illness'),
    })
    .addField({
      name: 'allergy',
      label: 'Allergy',
      type: 'text',
      tab: NURSE_INTAKE_TAB,
      placeholder: 'List any allergies (Leave blank if no allergies)',
      disabled: disabled('allergy'),
    })
    .addField({
      name: 'travel_outside_sl_30_days',
      label: 'Travel outside SL in last 30 days',
      type: 'text',
      tab: NURSE_INTAKE_TAB,
      placeholder: 'List any travel outside SL in last 30 days (Leave blank if no travel)',
      disabled: disabled('travel_outside_sl_30_days'),
    })
    .addField({ name: 'symptoms', label: 'Symptoms', type: 'textarea', tab: 'Treatment', maxRows: 5 })
    .addField({
      name: 'temperature',
      label: 'Temperature',
      type: 'number',
      required: false,
      tab: 'Vitals',
      disabled: disabled('temperature'),
    })
    .addField({
      name: 'blood_pressure_systolic',
      label: 'BP-Systolic',
      type: 'text',
      required: false,
      tab: 'Vitals',
      disabled: disabled('blood_pressure_systolic'),
    })
    .addField({
      name: 'blood_pressure_diastolic',
      label: 'BP-Diastolic',
      type: 'text',
      required: false,
      tab: 'Vitals',
      disabled: disabled('blood_pressure_diastolic'),
    })
    .addField({
      name: 'pulse',
      label: 'Pulse',
      type: 'number',
      required: false,
      tab: 'Vitals',
      disabled: disabled('pulse'),
    })
    .addField({
      name: 'weight',
      label: 'Weight',
      type: 'number',
      required: false,
      tab: 'Vitals',
      disabled: disabled('weight'),
    })
    .addField({
      name: 'spo2',
      label: 'SPO2',
      type: 'number',
      required: false,
      tab: 'Vitals',
      disabled: disabled('spo2'),
    })
    .addField({ name: 'diagnosis', label: 'Diagnosis', type: 'textarea', required: false, tab: 'Treatment', maxRows: 5 })
    .addField({ name: 'proposed_treatment', label: 'Treatment Plan', type: 'textarea', required: false, tab: 'Treatment', maxRows: 5 })
    .addField({ name: 'invoiced', label: 'Invoice?', type: 'checkbox', required: false, tab: 'Admin' })
    .addField({ name: 'updatedBy', label: 'Updated By', type: 'text', required: false, tab: 'Admin' })
    .addField({ name: 'updatedAt', label: 'Updated At', type: 'datetime', required: false, tab: 'Admin', disabled: true })
    .addField({ name: 'createdAt', label: 'Created At', type: 'datetime', required: false, tab: 'Admin', disabled: true })
    .addField({
      name: 'prescription_items',
      label: 'Medicine',
      type: 'advanced-array',
      endpoint: '/hospital-portal/api/items/search/by-name',
      tab: 'Prescriptions',
      additionalFields: [
        { name: 'quantity', label: 'Quantity', type: 'number', width: '5%' },
        { name: 'instructions', label: 'Dosage', type: 'text', width: '35%' },
        { name: 'duration', label: 'Duration', type: 'text', width: '15%' },
      ],
    });
};

export const configurePatientVisitTabs = (config) => {
  config
    .addTab({ name: 'Visit Details', securityPath: '/patient-visits/visit-details', permission: 'View', default: true })
    .addTab({ name: 'Vitals', securityPath: '/patient-visits/vitals', permission: 'View' })
    .addTab({
      name: NURSE_INTAKE_TAB,
      securityPath: '/patient-visits/chief-complaint',
      permission: 'View',
    })
    .addTab({ name: 'Treatment', securityPath: '/patient-visits/treatment', permission: 'View' })
    .addTab({ name: 'Prescriptions', securityPath: '/patient-visits/prescriptions', permission: 'View' })
    .addTab({ name: 'Admin', securityPath: '/patient-visits/admin', permission: 'View' });
};

export const configurePatientVisitAiActions = (config) => {
  config.addAiAction({
    tab: 'Treatment',
    featureKey: 'treatment-suggestions',
    buttonLabel: 'Refine Notes',
    endpoint: '/hospital-portal/api/ai/treatment-suggestions',
    payloadBuilder: (formData) => ({ visit_id: formData.visit_id, patient_id: formData.patient_id }),
    fieldMapping: {
      refined_symptoms: 'symptoms',
      suggested_diagnosis: 'diagnosis',
      suggested_treatment: 'proposed_treatment',
    },
    permissionKey: '/ai-assist/treatment-suggestions',
    permissionAction: 'Execute',
  });

  config.addAiAction({
    tab: 'Prescriptions',
    featureKey: 'prescription-analysis',
    buttonLabel: 'Analyze Prescription',
    buttonIcon: '🔬',
    mode: 'report',
    panelTitle: 'Prescription Analysis',
    endpoint: '/hospital-portal/api/ai/prescription-analysis',
    payloadBuilder: (formData) => ({
      visit_id: formData.visit_id,
      patient_id: formData.patient_id,
      medications: (formData.prescription_items || [])
        .filter((item) => item.name)
        .map((item) => item.name),
      symptoms: formData.symptoms || '',
      diagnosis: formData.diagnosis || '',
      treatment: formData.proposed_treatment || '',
    }),
    sections: [
      { key: 'adverse_reactions', title: 'Adverse Reactions', icon: '⚠️' },
      { key: 'drug_interactions', title: 'Drug Interactions', icon: '💊' },
      { key: 'warnings', title: 'Warnings & Notes', icon: '📋' },
    ],
    reasoningKey: 'summary',
    confidenceKey: 'severity',
    fieldMapping: {},
    permissionKey: '/ai-assist/prescription-analysis',
    permissionAction: 'Execute',
  });
};

export const getReferralLockMessage = (formData) => {
  const doctor = formData?.outgoing_referral_doctor;
  if (!doctor) return null;
  return `Visit is referred to ${doctor}. A new visit is required to continue treatment.`;
};

export const configurePatientVisitTabAlerts = (config) => {
  config.addTabAlert({
    tab: '*',
    className: 'form-tab-alert--warning',
    getMessage: getReferralLockMessage,
  });
  config.addTabAlert({
    tab: 'Treatment',
    getMessage: (formData) => formatAbnormalVitalsLabel(formData),
  });
};

export const configurePatientVisitForm = (config, options = {}) => {
  configurePatientVisitDropdowns(config);
  configurePatientVisitFields(config, options);
  configurePatientVisitTabs(config);
  configurePatientVisitAiActions(config);
  configurePatientVisitTabAlerts(config);
  config.isReadOnly = (formData) => Boolean(formData?.outgoing_referral_doctor);
  config.additionalValidation = async (formData) => validatePatientVisitForm(formData);
};
